import dayjs, { Dayjs } from "utils/dayjs"

import { Guid, IHydratable, Lmid } from "@/types"
import { Currency, InvoicingType, Purpose } from "config/constants"
import InvoiceDraftAsset, { IGetInvoiceDraftAssetDto } from "./InvoiceDraftAsset"
import i18n from "intl/i18n"
import DowntimePeriod, { IGetDowntimePeriodDto } from "./DowntimePeriod"
import InvoiceDraftInvoicePosition, { IGetInvoiceDraftInvoicePositionDto } from "./InvoiceDraftInvoicePosition"
import CustomerExtended, { ICustomerExtendedDto } from "./CustomerExtended"
import { InvoiceRateCalculationType, IOutgoingInvoiceCustomer } from "./OutgoingInvoice"
import { InvoicePositionDistributionRule } from './TenantInvoicePosition';

export enum OutgoingInvoiceDraftSortBy {
    requestNumber = "requestNumber",
    purpose = "purpose",
    customerName = "customerName",
    start = "start",
    end = "end",
    article = "article",
    lmid = "lmid",
    reservationStart = "reservationStart",
    reservationEnd = "reservationEnd",
    customerNumber = "customerNumber"
}

interface IGetOutgoingInvoiceDraftBaseDto {
    reservationId: Guid
    number: string
    purpose: string
    start: string
    end?: string | null
    articleId: Guid
    amount: number,
    distributionRule: InvoicePositionDistributionRule
    automatic: boolean
    invoiceRateCalculationType?: InvoiceRateCalculationType | null
    invoicingType: InvoicingType
    invoiceBlocked: boolean
    invoiceBlockedBy?: string | null
    invoiceBlockedDate?: string | null
    invoiceBlockedReason?: string | null
    invoiceBeforeShipping: boolean
    currency?: Currency | null
}

class OutgoingInvoiceDraftBase {
    reservationId: Guid
    number: string
    purpose: Purpose
    start: Dayjs
    end?: Dayjs
    articleId: Guid
    amount: number
    distributionRule: InvoicePositionDistributionRule
    automatic: boolean
    invoiceRateCalculationType?: InvoiceRateCalculationType
    invoicingType: InvoicingType
    invoiceBlocked: boolean
    invoiceBlockedBy?: string
    invoiceBlockedDate?: Dayjs
    invoiceBlockedReason?: string
    invoiceBeforeShipping?: boolean
    currency?: Currency

    constructor(dto: IGetOutgoingInvoiceDraftBaseDto) {
        this.reservationId = dto.reservationId
        this.number = dto.number
        this.purpose = dto.purpose as Purpose
        this.start = dayjs.utc(dto.start, "YYYY-MM-DD") // take only date ignoring the time
        this.end = dto.end ? dayjs.utc(dto.end, "YYYY-MM-DD") : undefined // take only date ignoring the time
        this.articleId = dto.articleId
        this.amount = dto.amount
        this.distributionRule = dto.distributionRule
        this.automatic = dto.automatic
        this.invoiceRateCalculationType = dto.invoiceRateCalculationType ?? undefined
        this.invoicingType = dto.invoicingType
        this.invoiceBlocked = dto.invoiceBlocked
        this.invoiceBlockedBy = dto.invoiceBlockedBy ?? undefined
        this.invoiceBlockedDate = dto.invoiceBlockedDate ? dayjs(dto.invoiceBlockedDate) : undefined
        this.invoiceBlockedReason = dto.invoiceBlockedReason ?? undefined
        this.invoiceBeforeShipping = dto.invoiceBeforeShipping
        this.currency = dto.currency ?? undefined
    }

    getExchangeRateDate({ purpose, disposalDate }: { purpose: Purpose, disposalDate?: Dayjs | null }): Dayjs | null {
        if (purpose === Purpose.sale) {
            return disposalDate ?? null
        } else {
            return dayjs().startOf("day")
        }
    }
}


export interface IGetOutgoingInvoiceDraftDto extends IGetOutgoingInvoiceDraftBaseDto {
    assets?: IGetInvoiceDraftAssetDto[] | null
    invoicePositions?: IGetInvoiceDraftInvoicePositionDto[] | null
    downtimePeriods?: Pick<IGetDowntimePeriodDto, "description" | "start" | "end" | "discount">[] | null
    downtimeDays?: number | null
    downtimeText?: string | null
    invoiceHeaderText?: string | null
    invoiceFooterText?: string | null
    customer: ICustomerExtendedDto
    costUnit?: string | null
}

export class OutgoingInvoiceDraft extends OutgoingInvoiceDraftBase implements IHydratable {
    assets: InvoiceDraftAsset[]
    invoicePositions: InvoiceDraftInvoicePosition[]
    downtimePeriods: DowntimePeriod[]
    downtimeDays: number
    downtimeText?: string | null
    invoiceHeaderText?: string | null
    invoiceFooterText?: string | null
    customer: CustomerExtended
    costUnit?: string

    constructor(dto: IGetOutgoingInvoiceDraftDto) {
        super(dto)
        this.assets = dto.assets?.map(invoiceDraftAssetDto => new InvoiceDraftAsset(invoiceDraftAssetDto)) || []
        this.invoicePositions = dto.invoicePositions?.map(invoicePositionDto => new InvoiceDraftInvoicePosition(invoicePositionDto)) || []
        this.downtimePeriods = dto.downtimePeriods?.map(downtimePeriodDto => new DowntimePeriod({
            ...downtimePeriodDto,
            id: ""
        })) || []
        this.downtimeDays = dto.downtimeDays ?? 0
        this.downtimeText = dto.downtimeText
        this.invoiceHeaderText = dto.invoiceHeaderText
        this.invoiceFooterText = dto.invoiceFooterText
        this.customer = new CustomerExtended(dto.customer)
        this.costUnit = dto.costUnit ?? undefined
    }

    get isHydrated(): boolean {
        return !!this.assets.some(asset => asset.isHydrated)
    }

    get invoicePeriodTotalDays() {
        return this.end ? this.end.diff(this.start, "days") + 1 : 0
    }
}

export interface IGetOutgoingInvoiceDraftItemDto extends IGetOutgoingInvoiceDraftBaseDto {
    reservationStart: string
    reservationEnd?: string | null
    salesAdminNumber: string
    customer: IOutgoingInvoiceCustomer
    assetId?: Guid | null
    lmid?: Lmid | null
    articleDescription: string
}

export class OutgoingInvoiceDraftItem extends OutgoingInvoiceDraftBase {
    reservationStart: Dayjs
    reservationEnd?: Dayjs
    customer: IOutgoingInvoiceCustomer
    assetId?: Guid
    lmid?: Lmid
    articleDescription: string
    salesAdminName: string
    invoiceBeforeShipping: boolean

    constructor(dto: IGetOutgoingInvoiceDraftItemDto, params: {
        salesAdminName: string
    }) {
        super(dto)
        this.reservationStart = dayjs.utc(dto.reservationStart)
        this.reservationEnd = dto.reservationEnd ? dayjs.utc(dto.reservationEnd) : undefined
        this.customer = dto.customer
        this.assetId = dto.assetId ?? undefined
        this.lmid = dto.lmid ?? undefined
        this.articleDescription = dto.articleDescription
        this.salesAdminName = params.salesAdminName
        this.invoiceBeforeShipping = dto.invoiceBeforeShipping ?? false
    }

    printDate() {
        if (!this.end) {
            return this.start.format(i18n.t('config.dateLongFormat'))
        }
        return `${this.start.format(i18n.t('config.dateFormat'))} – ${this.end.format(i18n.t('config.dateFormat'))}`
    }

    get isFirstInvoice(): boolean {
        return !!this.reservationStart.isSameOrAfter(this.start, "date")
    }

    get isLastInvoice(): boolean {
        return !!this.reservationEnd?.isSameOrBefore(this.end, "date")
    }
}
